import { DeviceMeasurementErrorDto, DevicePresenceStateDto } from '@/api/generator';
import { Ref, computed, ref, watch } from 'vue';
import { NotificationMessage } from '@/components/notifications/types';
import { useTranslation } from 'i18next-vue';
import { I18nKey } from '@/utils/typings';
import { severitiyNotificationTypeMapping, severityOrder } from './severity';
import { truthy } from '@/utils/helper';
import { orderCompare } from '@/utils/compareAndSort';
import { getNotificationDetails } from '@/components/notifications/utils';

export function useDeviceError(devicePresenceState: Ref<DevicePresenceStateDto | undefined>) {
  const { t } = useTranslation();

  function hasMessageDetails(error?: DeviceMeasurementErrorDto) {
    return !!error?.cause || !!error?.remedy;
  }

  function getNotificationForDeviceError(error: DeviceMeasurementErrorDto, detailed: boolean) {
    const { message, cause, remedy, code, severity } = error;

    if (severity) {
      const t_cause = t(cause as I18nKey, '', { code: code });
      const t_remedy = t(remedy as I18nKey);
      return {
        type: severitiyNotificationTypeMapping[severity],
        title: detailed ? t(message as I18nKey) : undefined,
        message: detailed ? getNotificationDetails(t_cause, t_remedy) : t(message as I18nKey),
        closable: false,
      };
    }
  }

  const messages = ref<DeviceMeasurementErrorDto[]>([]);

  watch(
    () => devicePresenceState.value?.messages,
    (newMessages) => {
      if (newMessages) {
        messages.value = newMessages;
      }
    },
    {
      immediate: true,
    },
  );

  const notifications = computed<NotificationMessage[] | undefined>(() => {
    return messages.value?.reduce<NotificationMessage[] | undefined>(
      (allNotifications, error) =>
        [...(allNotifications ?? []), getNotificationForDeviceError(error, true)].filter(truthy),
      undefined,
    );
  });

  const highestSeverityMessage = computed(() => {
    return messages.value
      ?.toSorted((a, b) =>
        orderCompare(severityOrder)(a.severity ?? 'Unknown', b.severity ?? 'Unknown'),
      )
      .at(0);
  });

  const highestSeverityNotification = computed(() =>
    highestSeverityMessage.value
      ? getNotificationForDeviceError(highestSeverityMessage.value, false)
      : undefined,
  );

  return {
    messages,
    notifications,
    highestSeverityMessage,
    highestSeverityNotification,
    hasMessageDetails,
  };
}
